import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import TimelineModal from '../TimelineModal/TimelineModal';

// Main Component
interface ProjectTimelineProps {
  startDate: any;
  endDate: any;
  onChangeStartDate?: (date: string) => void;
  onChangeEndDate?: (date: string) => void;
  errorStartDate?: string;
  errorEndDate?: string;
}

export default function ProjectTimeline({
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  errorStartDate,
  errorEndDate,
}: ProjectTimelineProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  // Local states derived from form value
  const [localStartDate, setLocalStartDate] = useState(
    startDate ? dayjs(startDate) : dayjs()
  );
  const [localEndDate, setLocalEndDate] = useState(
    endDate ? dayjs(endDate) : dayjs().add(3, 'month')
  );

  // Keep local state in sync when form value changes externally
  useEffect(() => {
    if (startDate) setLocalStartDate(dayjs(startDate));
    if (endDate) setLocalEndDate(dayjs(endDate));
  }, [startDate, endDate]);

  const handleSave = () => {
    setSaved(true);
    setIsOpen(false);

    // Push data into form as separate fields
    onChangeStartDate?.(localStartDate.toISOString());
    onChangeEndDate?.(localEndDate.toISOString());
  };

  const hasError = errorStartDate || errorEndDate;

  return (
    <>
      {saved ? (
        <div className="w-full flex flex-col items-start gap-1">
          <label className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-text-primary-heading font-semibold">
            Project Timeline <span className="text-sm text-error-1">*</span>
          </label>
          <div className="w-full flex items-center gap-3">
            <div
              onClick={() => setIsOpen(true)}
              className={`cursor-pointer flex items-center gap-2 w-full bg-modal-input text-sm text-input-active-placeholder rounded-md py-[10px] px-[14px] border ${
                errorStartDate ? 'border-error-2' : 'border-gray-3/20'
              }`}
            >
              <Calendar /> Start Date
              <span className="mx-2 border-r border-[#3E3E3E] h-5" />
              {localStartDate.format('DD MMMM, YYYY')}
            </div>
            <div
              onClick={() => setIsOpen(true)}
              className={`cursor-pointer flex items-center gap-2 w-full bg-modal-input text-sm text-input-active-placeholder rounded-md py-[14px] px-[14px] border ${
                errorEndDate ? 'border-error-2' : 'border-gray-3/20'
              }`}
            >
              <Calendar /> End Date
              <span className="mx-2 border-r border-[#3E3E3E] h-5" />
              {localEndDate.format('DD MMMM, YYYY')}
            </div>
          </div>
          {hasError && (
            <p className="text-error-2 text-xs mt-1">
              {errorStartDate || errorEndDate}
            </p>
          )}
        </div>
      ) : (
        <div
          onClick={() => setIsOpen(true)}
          className="w-full flex flex-col items-start gap-1"
        >
          <label className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold">
            Project Timeline <span className="text-sm text-error-1">*</span>
          </label>
          <div
            className={`cursor-pointer flex items-center text-input-inactive-placeholder gap-2 w-full bg-modal-input rounded-md py-[14px] px-[14px]  ${
              hasError
                ? 'border-0.5 border-error-2'
                : 'border-0.5 border-gray-3/20'
            }`}
          >
            <Calendar /> Enter project timeline
          </div>
          {hasError && (
            <p className="text-error-2 text-xs mt-1">
              {errorStartDate || errorEndDate}
            </p>
          )}
        </div>
      )}

      {/* ====== Modal ===== */}
      <TimelineModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        localStartDate={localStartDate}
        localEndDate={localEndDate}
        onStartDateChange={setLocalStartDate}
        onEndDateChange={setLocalEndDate}
        onSave={handleSave}
      />
    </>
  );
}

function Calendar() {
  return (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.66602 5.29175C6.32435 5.29175 6.04102 5.00841 6.04102 4.66675V2.16675C6.04102 1.82508 6.32435 1.54175 6.66602 1.54175C7.00768 1.54175 7.29102 1.82508 7.29102 2.16675V4.66675C7.29102 5.00841 7.00768 5.29175 6.66602 5.29175Z"
        fill="#3E3E3E"
      />
      <path
        d="M13.334 5.29175C12.9923 5.29175 12.709 5.00841 12.709 4.66675V2.16675C12.709 1.82508 12.9923 1.54175 13.334 1.54175C13.6757 1.54175 13.959 1.82508 13.959 2.16675V4.66675C13.959 5.00841 13.6757 5.29175 13.334 5.29175Z"
        fill="#3E3E3E"
      />
      <path
        d="M7.08333 12.5834C6.975 12.5834 6.86667 12.5584 6.76667 12.5168C6.65833 12.4751 6.575 12.4168 6.49167 12.3418C6.34167 12.1834 6.25 11.9751 6.25 11.7501C6.25 11.6418 6.275 11.5334 6.31667 11.4334C6.35833 11.3334 6.41667 11.2418 6.49167 11.1584C6.575 11.0834 6.65833 11.0251 6.76667 10.9834C7.06667 10.8584 7.44167 10.9251 7.675 11.1584C7.825 11.3168 7.91667 11.5334 7.91667 11.7501C7.91667 11.8001 7.90833 11.8584 7.9 11.9168C7.89167 11.9668 7.875 12.0168 7.85 12.0668C7.83333 12.1168 7.80833 12.1668 7.775 12.2168C7.75 12.2584 7.70833 12.3001 7.675 12.3418C7.51667 12.4918 7.3 12.5834 7.08333 12.5834Z"
        fill="#3E3E3E"
      />
      <path
        d="M9.99935 12.5832C9.89102 12.5832 9.78268 12.5582 9.68268 12.5166C9.57435 12.4749 9.49102 12.4166 9.40768 12.3416C9.25768 12.1832 9.16602 11.9749 9.16602 11.7499C9.16602 11.6416 9.19102 11.5332 9.23268 11.4332C9.27435 11.3332 9.33268 11.2416 9.40768 11.1582C9.49102 11.0832 9.57435 11.0249 9.68268 10.9832C9.98268 10.8499 10.3577 10.9249 10.591 11.1582C10.741 11.3166 10.8327 11.5332 10.8327 11.7499C10.8327 11.7999 10.8243 11.8582 10.816 11.9166C10.8077 11.9666 10.791 12.0166 10.766 12.0666C10.7493 12.1166 10.7243 12.1666 10.691 12.2166C10.666 12.2582 10.6243 12.2999 10.591 12.3416C10.4327 12.4916 10.216 12.5832 9.99935 12.5832Z"
        fill="#3E3E3E"
      />
      <path
        d="M12.9173 12.5832C12.809 12.5832 12.7007 12.5582 12.6007 12.5166C12.4923 12.4749 12.409 12.4166 12.3257 12.3416C12.2923 12.2999 12.259 12.2582 12.2256 12.2166C12.1923 12.1666 12.1673 12.1166 12.1507 12.0666C12.1257 12.0166 12.109 11.9666 12.1007 11.9166C12.0923 11.8582 12.084 11.7999 12.084 11.7499C12.084 11.5332 12.1757 11.3166 12.3257 11.1582C12.409 11.0832 12.4923 11.0249 12.6007 10.9832C12.909 10.8499 13.2757 10.9249 13.509 11.1582C13.659 11.3166 13.7507 11.5332 13.7507 11.7499C13.7507 11.7999 13.7423 11.8582 13.734 11.9166C13.7257 11.9666 13.709 12.0166 13.684 12.0666C13.6673 12.1166 13.6423 12.1666 13.609 12.2166C13.584 12.2582 13.5423 12.2999 13.509 12.3416C13.3507 12.4916 13.134 12.5832 12.9173 12.5832Z"
        fill="#3E3E3E"
      />
      <path
        d="M7.08333 15.5C6.975 15.5 6.86667 15.475 6.76667 15.4333C6.66667 15.3917 6.575 15.3333 6.49167 15.2583C6.34167 15.1 6.25 14.8833 6.25 14.6667C6.25 14.5583 6.275 14.45 6.31667 14.35C6.35833 14.2417 6.41667 14.15 6.49167 14.075C6.8 13.7667 7.36667 13.7667 7.675 14.075C7.825 14.2333 7.91667 14.45 7.91667 14.6667C7.91667 14.8833 7.825 15.1 7.675 15.2583C7.51667 15.4083 7.3 15.5 7.08333 15.5Z"
        fill="#3E3E3E"
      />
      <path
        d="M9.99935 15.5C9.78268 15.5 9.56602 15.4083 9.40768 15.2583C9.25768 15.1 9.16602 14.8833 9.16602 14.6667C9.16602 14.5583 9.19102 14.45 9.23268 14.35C9.27435 14.2417 9.33268 14.15 9.40768 14.075C9.71602 13.7667 10.2827 13.7667 10.591 14.075C10.666 14.15 10.7243 14.2417 10.766 14.35C10.8077 14.45 10.8327 14.5583 10.8327 14.6667C10.8327 14.8833 10.741 15.1 10.591 15.2583C10.4327 15.4083 10.216 15.5 9.99935 15.5Z"
        fill="#3E3E3E"
      />
      <path
        d="M12.9173 15.4999C12.7007 15.4999 12.484 15.4083 12.3257 15.2583C12.2507 15.1833 12.1923 15.0916 12.1507 14.9833C12.109 14.8833 12.084 14.7749 12.084 14.6666C12.084 14.5583 12.109 14.4499 12.1507 14.3499C12.1923 14.2416 12.2507 14.1499 12.3257 14.0749C12.5173 13.8833 12.809 13.7916 13.0757 13.8499C13.134 13.8583 13.184 13.8749 13.234 13.8999C13.284 13.9166 13.334 13.9416 13.384 13.9749C13.4257 13.9999 13.4673 14.0416 13.509 14.0749C13.659 14.2333 13.7507 14.4499 13.7507 14.6666C13.7507 14.8833 13.659 15.0999 13.509 15.2583C13.3507 15.4083 13.134 15.4999 12.9173 15.4999Z"
        fill="#3E3E3E"
      />
      <path
        d="M17.0827 8.69995H2.91602C2.57435 8.69995 2.29102 8.41662 2.29102 8.07495C2.29102 7.73328 2.57435 7.44995 2.91602 7.44995H17.0827C17.4243 7.44995 17.7077 7.73328 17.7077 8.07495C17.7077 8.41662 17.4243 8.69995 17.0827 8.69995Z"
        fill="#3E3E3E"
      />
      <path
        d="M13.3333 19.4584H6.66667C3.625 19.4584 1.875 17.7084 1.875 14.6667V7.58342C1.875 4.54175 3.625 2.79175 6.66667 2.79175H13.3333C16.375 2.79175 18.125 4.54175 18.125 7.58342V14.6667C18.125 17.7084 16.375 19.4584 13.3333 19.4584ZM6.66667 4.04175C4.28333 4.04175 3.125 5.20008 3.125 7.58342V14.6667C3.125 17.0501 4.28333 18.2084 6.66667 18.2084H13.3333C15.7167 18.2084 16.875 17.0501 16.875 14.6667V7.58342C16.875 5.20008 15.7167 4.04175 13.3333 4.04175H6.66667Z"
        fill="#3E3E3E"
      />
    </svg>
  );
}
